<template>
  <div class="lep-view">

    <div class="lep-columns">

      <!-- ─── Left: Form fields ─── -->
      <div class="lep-form">

        <!-- Instagram Unified Lock Hint (Positioned ABOVE Location Name) -->
        <div class="lep-address-locked-notice" v-if="currentNetwork === 'instagram'">
          <i class="material-icons lep-lock-icon">lock</i>
          <div class="lep-address-locked-content">
            <span class="lep-address-locked-title">{{ $t('dash.presenceManagement.location.profile.instagramLockedTitle') }}</span>
            <p class="lep-address-locked-text">{{ $t('dash.presenceManagement.location.profile.instagramLockedHint') }}</p>
          </div>
        </div>

        <!-- Location Name -->
        <div class="lep-field">
          <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.name') }}</label>
          <input 
            type="text" 
            v-model="localName" 
            class="lep-input" 
            :class="{ 'lep-input--disabled': currentNetwork === 'facebook' || currentNetwork === 'instagram' }"
            :disabled="currentNetwork === 'facebook' || currentNetwork === 'instagram'"
          />
          <div class="lep-address-locked-notice" v-if="currentNetwork === 'facebook'" style="margin-top: 8px; margin-bottom: 0;">
            <i class="material-icons lep-lock-icon">lock</i>
            <div class="lep-address-locked-content">
              <p class="lep-address-locked-text">{{ $t('dash.presenceManagement.location.profile.facebookNameLockedHint') }}</p>
            </div>
          </div>
        </div>

        <!-- Instagram Handle (@username) -->
        <div class="lep-field" v-if="currentNetwork === 'instagram'">
          <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.instagramUsername') }}</label>
          <input 
            type="text" 
            :value="'@' + (locationData.username || localName)" 
            class="lep-input lep-input--disabled" 
            disabled 
          />
        </div>

        <!-- Facebook Address Category Hint (positioned under location name, before address fields) -->
        <div class="lep-sab-notice" v-if="currentNetwork === 'facebook'">
          <i class="material-icons">info_outline</i>
          <span>{{ $t('dash.presenceManagement.location.profile.facebookAddressHint') }}</span>
        </div>

        <template v-if="!isServiceAreaOnly">
          <!-- Locked Address Security Notice -->
          <div class="lep-address-locked-notice" v-if="isAddressLocked">
            <i class="material-icons lep-lock-icon">lock</i>
            <div class="lep-address-locked-content">
              <span class="lep-address-locked-title">{{ $t('dash.presenceManagement.location.profile.addressLockedTitle') }}</span>
              <p class="lep-address-locked-text">{{ $t('dash.presenceManagement.location.profile.addressLockedText') }}</p>
            </div>
          </div>

          <!-- Address -->
          <div class="lep-field">
            <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.address') }}</label>
            <input 
              type="text" 
              v-model="localAddress" 
              class="lep-input" 
              :class="{ 'lep-input--error': addressErrors.address, 'lep-input--disabled': isAddressLocked }"
              :disabled="isAddressLocked"
            />
          </div>

          <!-- ZIP -->
          <div class="lep-row-fields">
            <div class="lep-field">
              <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.zip') }}</label>
              <input 
                type="text" 
                v-model="localZip" 
                class="lep-input" 
                :class="{ 'lep-input--error': addressErrors.zip, 'lep-input--disabled': isAddressLocked }"
                :disabled="isAddressLocked"
                @input="onZipInput"
                @focus="showCitySuggestions = true"
                @blur="onCityBlur"
              />
            </div>
            <div class="lep-field lep-city-wrapper">
              <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.city') }}</label>
              <div class="lep-city-input-rel">
                <input 
                  type="text" 
                  v-model="localCity" 
                  class="lep-input" 
                  :class="{ 'lep-input--error': addressErrors.city, 'lep-input--disabled': isAddressLocked }"
                  :disabled="isAddressLocked"
                  @input="onCityInput"
                  @focus="showCitySuggestions = true"
                  @blur="onCityBlur"
                />
                <i v-if="loadingCitySuggestions" class="material-icons lep-city-search-icon lep-spinner">sync</i>
                <div v-if="showCitySuggestions && citySuggestions.length" class="lep-city-suggestions">
                  <div
                    v-for="city in citySuggestions"
                    :key="city.id"
                    class="lep-city-suggestion-item"
                    @mousedown.prevent="selectCitySuggestion(city)"
                  >
                    <i class="material-icons lep-city-icon">location_on</i>
                    <div class="lep-city-text">
                      <span class="lep-city-name">{{ city.name }}</span>
                      <span class="lep-city-subtext">{{ city.postalCode ? city.postalCode + ' - ' : '' }}{{ city.region }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="lep-sab-notice" v-if="currentNetwork !== 'instagram'">
            <i class="material-icons">info</i>
            <span>{{ sabNoticeText }}</span>
          </div>
        </template>

        <!-- Phone, Email, WhatsApp (Stacked vertically in full-width fields) -->
        <template v-if="currentNetwork === 'facebook' || currentNetwork === 'instagram'">
          <div class="lep-field">
            <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.phone') }}</label>
            <input type="text" v-model="localPhone" class="lep-input" />
          </div>
          <div class="lep-field">
            <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.facebookEmail') }}</label>
            <input type="email" v-model="localEmail" class="lep-input" placeholder="contact@exemple.fr" />
          </div>
          <div class="lep-field" v-if="currentNetwork === 'facebook'">
            <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.facebookWhatsapp') }}</label>
            <input type="text" v-model="localWhatsapp" class="lep-input" placeholder="+33612345678" />
          </div>
        </template>
        <template v-else>
          <div class="lep-field">
            <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.phone') }}</label>
            <input type="text" v-model="localPhone" class="lep-input" />
          </div>
        </template>

        <!-- Description -->
        <div class="lep-field">
          <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.description') }}</label>
          <div class="lep-textarea-wrapper">
            <textarea
              v-model="localDescription"
              class="lep-textarea"
              :maxlength="currentNetwork === 'instagram' ? 150 : 750"
              rows="5"
            ></textarea>
            <span class="lep-char-count">{{ localDescription.length }}/{{ currentNetwork === 'instagram' ? 150 : 750 }}</span>
          </div>
        </div>

        <!-- Website -->
        <div class="lep-field">
          <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.website') }}</label>
          <input type="url" v-model="localWebsite" class="lep-input" placeholder="https://" />
        </div>

        <!-- Facebook & Instagram (50 / 50 equal split) -->
        <div class="lep-row-fields lep-row-fields--2" v-if="currentNetwork !== 'instagram'">
          <div class="lep-field">
            <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.facebook') }}</label>
            <input type="text" v-model="localFacebook" class="lep-input" :placeholder="$t('dash.presenceManagement.location.profile.handlePlaceholder')" />
          </div>
          <div class="lep-field">
            <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.instagram') }}</label>
            <input type="text" v-model="localInstagram" class="lep-input" :placeholder="$t('dash.presenceManagement.location.profile.handlePlaceholder')" />
          </div>
        </div>

        <!-- Facebook Specific Fields -->
        <template v-if="currentNetwork === 'facebook'">
          <!-- Custom Links Section (jusqu'à 10 liens) -->
          <div class="lep-custom-links-section">
            <div class="lep-custom-links-header">
              <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.customLinksTitle', { count: localCustomLinks.length }) }}</label>
              <button 
                type="button" 
                class="lep-btn-add-link"
                v-if="localCustomLinks.length < 10" 
                @click="addCustomLink"
              >
                <i class="material-icons">link</i>
                <span>{{ $t('dash.presenceManagement.location.profile.addCustomLink') }}</span>
              </button>
            </div>

            <div v-for="(link, idx) in localCustomLinks" :key="idx" class="lep-custom-link-card">
              <div class="lep-custom-link-fields">
                <div class="lep-field">
                  <label class="lep-sublabel">{{ $t('dash.presenceManagement.location.profile.customLinkDescLabel') }}</label>
                  <input 
                    type="text" 
                    v-model="link.description" 
                    class="lep-input" 
                    :placeholder="$t('dash.presenceManagement.location.profile.customLinkDescPlaceholder')" 
                  />
                </div>
                <div class="lep-field">
                  <label class="lep-sublabel">{{ $t('dash.presenceManagement.location.profile.customLinkUrlLabel') }}</label>
                  <input 
                    type="url" 
                    v-model="link.url" 
                    class="lep-input" 
                    placeholder="https://" 
                  />
                </div>
                  <button type="button" class="lep-btn-remove-link-text" @click="removeCustomLink(idx)">
                    <div class="lep-btn-inner">
                      <i class="material-icons">delete_outline</i>
                      <span>{{ $t('dash.presenceManagement.location.profile.removeCustomLink') }}</span>
                    </div>
                </button>
              </div>
            </div>
          </div>

          <!-- Date de fondation (placée sous liens web complémentaires) -->
          <div class="lep-field">
            <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.foundingDate') }}</label>
            <input type="date" v-model="localFoundingDate" class="lep-input" />
          </div>

          <!-- Politique de confidentialité & Mentions légales -->
          <div class="lep-field">
            <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.privacyPolicyUrl') }}</label>
            <input type="url" v-model="localPrivacyPolicyUrl" class="lep-input" placeholder="https://" />
          </div>

          <div class="lep-field">
            <div class="lep-label-row">
              <label class="lep-label">{{ $t('dash.presenceManagement.location.profile.impressum') }}</label>
              <small class="lep-char-count">{{ localImpressum.length }}/2000</small>
            </div>
            <textarea 
              v-model="localImpressum" 
              class="lep-textarea" 
              maxlength="2000"
              rows="3"
              :placeholder="$t('dash.presenceManagement.location.profile.impressumPlaceholder')"
            ></textarea>
          </div>
        </template>

      </div>

      <!-- ─── Sidebar columns wrapper ─── -->
      <div class="lep-sidebar-cols">
          <!-- ─── Middle: Smartphone mockup preview ─── -->
          <div class="lep-preview-col">
            <instagram-mockup-preview
              v-if="currentNetwork === 'instagram'"
              :name="localName"
              :username="locationData.username || locationData.handle || ''"
              :website="localWebsite"
              :phone="localPhone"
              :email="localEmail"
              :category-name="primaryCategoryName"
              :image-url="locationData.avatar || (locationData.metadata && locationData.metadata.profilePhotoUri) || ''"
              :cover-image-url="locationData.coverPhoto || (locationData.metadata && locationData.metadata.coverPhotoUri) || ''"
              :biography="localDescription"
              :followers-count="locationData.followersCount"
              :follows-count="locationData.followsCount"
              :instagram-uri="locationData.instagramUri || (locationData.socialProfiles && locationData.socialProfiles.instagram) || ''"
              :additional-photos="locationData.additionalPhotos || []"
            />
            <facebook-mockup-preview
              v-else-if="currentNetwork === 'facebook'"
              :name="localName"
              :address="computedAddress"
              :phone="localPhone"
              :website="localWebsite"
              :category-name="primaryCategoryName"
              :image-url="locationData.avatar || (locationData.metadata && locationData.metadata.profilePhotoUri) || ''"
              :cover-image-url="locationData.coverPhoto || (locationData.metadata && locationData.metadata.coverPhotoUri) || ''"
              :page-uri="locationData.facebookUri || (locationData.socialProfiles && locationData.socialProfiles.facebook) || ''"
              :additional-photos="locationData.additionalPhotos || []"
            />
            <apple-mockup-preview
              v-else-if="currentNetwork === 'apple'"
              :name="localName"
              :address="computedAddress"
              :phone="localPhone"
              :website="localWebsite"
              :description="localDescription"
              :category-name="primaryCategoryName"
              :image-url="locationData.avatar || (locationData.metadata && locationData.metadata.profilePhotoUri) || ''"
              :cover-image-url="locationData.coverPhoto || (locationData.metadata && locationData.metadata.coverPhotoUri) || ''"
              :maps-uri="locationData.metadata && locationData.metadata.mapsUri || ''"
              :additional-photos="locationData.additionalPhotos || []"
              :regular-hours="locationData.regularHours"
            />
            <google-mockup-preview
              v-else
              :name="localName"
              :address="computedAddress"
              :phone="localPhone"
              :website="localWebsite"
              :category-name="primaryCategoryName"
              :image-url="locationData.avatar || (locationData.metadata && locationData.metadata.profilePhotoUri) || ''"
              :cover-photo="locationData.coverPhoto || (locationData.metadata && locationData.metadata.coverPhotoUri) || ''"
              :additional-photos="locationData.additionalPhotos || []"
              :regular-hours="locationData.regularHours"
              :rating="locationData.rating"
              :review-count="locationData.reviewCount"
              :maps-uri="locationData.metadata && locationData.metadata.mapsUri || ''"
              @add-photo="$emit('add-photo')"
              @switch-tab="$emit('switch-tab', $event)"
            />
          </div>

          <!-- ─── Right: Score panel ─── -->
          <div class="lep-score-col">
            <div class="lep-score-panel">
              <div class="lep-score-header">
                <p class="lep-score-title">{{ $t('dash.presenceManagement.location.profile.scorePanel.title') }}</p>
                <div class="lep-score-donut-row">
                  <completion-donut :value="completionAudit.score" :band="completionAudit.band" />
                  <span class="lep-score-percent">{{ completionAudit.score }}%<br /><small>{{ $t('dash.presenceManagement.location.profile.scorePanel.completed') }}</small></span>
                </div>
              </div>

              <div class="lep-score-items">
                <div
                  v-for="item in scoreItems"
                  :key="item.key"
                  class="lep-score-item"
                >
                  <span class="lep-score-item-icon" :class="`lep-score-item-icon--${item.status}`">
                    <i class="material-icons">{{ item.status === 'ok' ? 'check' : 'priority_high' }}</i>
                  </span>
                  <span class="lep-score-item-label">{{ item.label }}</span>
                  <span v-if="item.badge" class="lep-score-item-badge">{{ item.badge }}</span>
                </div>
              </div>

              <div v-if="nextActions.length" class="lep-next-actions">
                <p class="lep-next-actions-title">{{ $t('dash.presenceManagement.location.profile.scorePanel.nextActions') }}</p>
                <ul class="lep-next-actions-list">
                  <li
                    v-for="(action, idx) in nextActions"
                    :key="idx"
                    class="lep-next-action-item"
                    @click="$emit('switch-tab', action.tab)"
                  >
                    <i class="material-icons lep-arrow-icon">arrow_forward</i>
                    <span class="lep-next-action-text">{{ action.label }}</span>
                    <small class="lep-next-action-weight">+{{ action.weight }}%</small>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import axios from 'axios'
import GoogleMockupPreview from '../components/GoogleMockupPreview.vue'
import FacebookMockupPreview from '../components/FacebookMockupPreview.vue'
import InstagramMockupPreview from '../components/InstagramMockupPreview.vue'
import AppleMockupPreview from '../components/AppleMockupPreview.vue'
import CompletionDonut from '../components/ui-core/CompletionDonut.vue'
import { computeCompletionAudit } from '@/store/modules/presenceManagement'

export default {
  name: 'tab-profile',

  components: { GoogleMockupPreview, FacebookMockupPreview, InstagramMockupPreview, AppleMockupPreview, CompletionDonut },

  props: {
    locationData: { type: Object, required: true },
    completionScore: { type: Number, default: 0 },
    completionBand: { type: String, default: 'low' }
  },

  data() {
    return {
      localName: this.locationData?.title || '',
      localAddress: this.locationData?.storefrontAddress?.addressLines?.join(' ') || '',
      localZip: this.locationData?.storefrontAddress?.postalCode || '',
      localCity: this.locationData?.storefrontAddress?.locality || '',
      localPhone: this.locationData?.phoneNumbers?.primaryPhone || '',
      localDescription: this.locationData?.profile?.description || '',
      localWebsite: this.locationData?.websiteUri || '',
      localFacebook: this.locationData?.socialProfiles?.facebook || '',
      localInstagram: this.locationData?.socialProfiles?.instagram || '',
      localEmail: this.locationData?.email || (Array.isArray(this.locationData?.emails) && this.locationData.emails[0]) || '',
      localWhatsapp: this.locationData?.whatsappNumber || '',
      localFoundingDate: this.locationData?.foundingDate || '',
      localPrivacyPolicyUrl: this.locationData?.privacyPolicyUrl || '',
      localImpressum: this.locationData?.impressum || '',
      localCustomLinks: Array.isArray(this.locationData?.customLinks)
        ? JSON.parse(JSON.stringify(this.locationData.customLinks))
        : [],
      addressErrors: {
        address: false,
        zip: false,
        city: false
      },
      selectedCityId: null,
      citySuggestions: [],
      showCitySuggestions: false,
      loadingCitySuggestions: false,
      citySearchTimeout: null
    }
  },

  computed: {
    // address() {
    //   const a = this.locationData?.storefrontAddress
    //   if (!a) return ''
    //   const lines = (a.addressLines || []).join(' ')
    //   return [lines, [a.postalCode, a.locality].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    // },

    computedAddress() {
      const lines = this.localAddress.trim()
      return [lines, [this.localZip, this.localCity].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    },

    isAddressLocked() {
      if (this.currentNetwork === 'facebook') return false
      const original = this.locationData?.storefrontAddress
      if (!original) return false
      const hasStreet = original.addressLines && original.addressLines.length > 0 && !!original.addressLines[0].trim()
      const hasCity = original.locality && !!original.locality.trim()
      const hasZip = original.postalCode && !!original.postalCode.trim()
      return !!(hasStreet && hasCity && hasZip)
    },

    primaryCategoryName() {
      return this.locationData?.categories?.primaryCategory?.displayName || ''
    },

    currentNetwork() {
      const net = this.locationData?.network || (Array.isArray(this.locationData?.networks) && this.locationData.networks[0]) || 'google'
      return String(net).toLowerCase()
    },

    isServiceAreaOnly() {
      if (this.currentNetwork === 'instagram') return true
      if (this.locationData?.serviceArea?.businessType === 'CUSTOMER_LOCATION_ONLY') return true
      return false
    },

    sabNoticeText() {
      if (this.currentNetwork === 'instagram') {
        return this.$t('dash.presenceManagement.location.profile.instagramSabNotice')
      }
      const category = this.primaryCategoryName
      if (category) {
        return this.$t('dash.presenceManagement.location.profile.sabNoticeCategory', { category })
      }
      return this.$t('dash.presenceManagement.location.profile.sabNotice')
    },

    completionAudit() {
      if (!this.locationData) return { score: 0, band: 'low', items: [], missingItems: [] }
      return computeCompletionAudit(this.locationData)
    },

    scoreItems() {
      return (this.completionAudit.items || []).map(item => ({
        key: item.key,
        label: this.$te(item.labelKey) ? this.$t(item.labelKey) : item.defaultLabel,
        status: item.passed ? 'ok' : 'warn',
        badge: `+${item.weight}%`
      }))
    },

    nextActions() {
      return (this.completionAudit.missingItems || []).slice(0, 3).map(item => ({
        key: item.key,
        label: this.$te(item.labelKey) ? this.$t(item.labelKey) : item.defaultLabel,
        weight: item.weight,
        tab: item.tab
      }))
    }
  },

  methods: {
    validate() {
      this.addressErrors = {
        address: false,
        zip: false,
        city: false
      }

      const hasAddress = !!this.localAddress.trim()
      const hasZip = !!this.localZip.trim()
      const hasCity = !!this.localCity.trim()

      if (hasAddress || hasZip || hasCity) {
        if (!hasAddress || !hasZip || !hasCity) {
          this.addressErrors.address = !hasAddress
          this.addressErrors.zip = !hasZip
          this.addressErrors.city = !hasCity

          this.$vs.notify({
            title: this.$t('dash.presenceManagement.location.error.incompleteAddressTitle'),
            text: this.$t('dash.presenceManagement.location.error.incompleteAddressText'),
            color: 'warning',
            position: 'top-right'
          })
          return false
        }
      }
      return true
    },

    onZipInput() {
      this.selectedCityId = null
      this.showCitySuggestions = true
      clearTimeout(this.citySearchTimeout)
      const q = (this.localZip || '').trim()
      if (q.length < 2) {
        this.citySuggestions = []
        return
      }
      this.citySearchTimeout = setTimeout(() => {
        this.fetchCitySuggestions(q)
      }, 300)
    },

    onCityInput() {
      this.selectedCityId = null
      this.showCitySuggestions = true
      clearTimeout(this.citySearchTimeout)
      const q = (this.localCity || '').trim()
      if (q.length < 2) {
        this.citySuggestions = []
        return
      }
      this.citySearchTimeout = setTimeout(() => {
        this.fetchCitySuggestions(q)
      }, 300)
    },

    async fetchCitySuggestions(query) {
      this.loadingCitySuggestions = true
      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_HOST}/presence-management/cities?q=${encodeURIComponent(query)}&country=FR&_t=${Date.now()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        )
        if (data && data.success && Array.isArray(data.data?.cities)) {
          this.citySuggestions = data.data.cities
        } else if (data && data.success && Array.isArray(data.cities)) {
          this.citySuggestions = data.cities
        } else {
          this.citySuggestions = []
        }
      } catch (err) {
        console.error('Error fetching city suggestions:', err)
        this.citySuggestions = []
      } finally {
        this.loadingCitySuggestions = false
      }
    },

    selectCitySuggestion(city) {
      this.localCity = city.name
      if (city.postalCode) {
        this.localZip = city.postalCode
      }
      this.selectedCityId = city.id
      this.showCitySuggestions = false
      this.citySuggestions = []
    },

    onCityBlur() {
      setTimeout(() => {
        this.showCitySuggestions = false
      }, 200)
    },

    addCustomLink() {
      if (this.localCustomLinks.length < 10) {
        this.localCustomLinks.push({ description: '', url: '' })
      }
    },

    removeCustomLink(index) {
      this.localCustomLinks.splice(index, 1)
    },

    /** Returns current profile data. Called by the parent on save. */
    getProfileData() {
      return {
        title: this.localName,
        email: this.localEmail,
        whatsappNumber: this.localWhatsapp,
        foundingDate: this.localFoundingDate,
        privacyPolicyUrl: this.localPrivacyPolicyUrl,
        impressum: this.localImpressum,
        customLinks: (this.localCustomLinks || []).filter(l => l.url.trim() || l.description.trim()),
        phoneNumbers: {
          ...(this.locationData.phoneNumbers || {}),
          primaryPhone: this.localPhone
        },
        storefrontAddress: {
          regionCode: 'FR',
          ...(this.locationData.storefrontAddress || {}),
          addressLines: [this.localAddress.trim()].filter(Boolean),
          postalCode: this.localZip,
          locality: this.localCity,
          cityId: this.selectedCityId || undefined
        },
        profile: { ...this.locationData.profile, description: this.localDescription },
        websiteUri: this.localWebsite,
        socialProfiles: {
          ...(this.locationData.socialProfiles || {}),
          facebook: this.localFacebook,
          instagram: this.localInstagram
        }
      }
    }
  }
}
</script>

<style scoped>
.lep-view { width: 100%; }

.lep-columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 28px;
  align-items: start;
  padding: 24px 28px;
}

.lep-sidebar-cols {
  display: flex;
  gap: 28px;
  align-items: stretch;
}

.lep-preview-col {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 280px;
}

.lep-score-col {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 260px;

  .cd-donut {
    transform: scale(.9);
  }

  .cd-value {
    display: none;
  }
}

/* ─── Form ─── */
.lep-form { display: flex; flex-direction: column; gap: 20px; min-width: 0; }

.lep-field { display: flex; flex-direction: column; gap: 6px; }

.lep-row-fields {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 12px;
}

.lep-row-fields--2 {
  grid-template-columns: 1fr 1fr !important;
}

.lep-row-fields--3 {
  grid-template-columns: repeat(3, 1fr) !important;
}

.lep-custom-links-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 12px 0;
  padding: 20px;
  background: #fdfbfd;
  border: 1px dashed #e2d5ef;
  border-radius: 12px;
}

.lep-custom-links-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.lep-btn-add-link {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 6px !important;
  width: auto !important;
  min-width: 0 !important;
  background: #6a1f4c !important;
  color: #f3e8fa !important;
  border: none !important;
  border-radius: 6px !important;
  padding: 8px 14px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
}

.lep-btn-add-link ::v-deep span {
  text-transform: none !important;

}

.lep-sublabel {
  font-size: 12px;
  font-weight: 600;
  color: #8c7d96;
}

.lep-custom-link-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
  border: 1px solid #e8e0ee;
  border-radius: 10px;
  padding: 16px;
  width: 100%;
  box-sizing: border-box;
}

.lep-custom-link-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.lep-custom-link-fields .lep-input {
  width: 100%;
  box-sizing: border-box;
}

.lep-btn-remove-link-text {
  display: inline-flex !important;
  justify-content: flex-end !important;
  align-items: center !important;
  background: #6a1f4c !important;
  background: #fff !important;
  color: #fff !important;
  color: #6a1f4c !important;
  border: none !important;
  border-radius: 6px !important;
  padding: 6px 14px !important;
  padding: 6px 5px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
}

.lep-btn-inner {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  .material-icons {
    font-size: 15px !important;
    margin-right: -115px !important;
  }
}

.lep-btn-inner span {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: inherit !important;
  text-transform: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

.lep-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.lep-city-input-rel {
  position: relative;
  width: 100%;
}

.lep-city-search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6a1f4c;
  font-size: 18px;
}

@keyframes lep-spin {
  from { transform: translateY(-50%) rotate(0deg); }
  to { transform: translateY(-50%) rotate(360deg); }
}

.lep-spinner {
  animation: lep-spin 1s linear infinite;
}

.lep-city-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e2d5ef;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(46, 25, 55, 0.15);
  max-height: 220px;
  overflow-y: auto;
  z-index: 100;
  margin-top: 4px;
}

.lep-city-suggestion-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f5eff9;
  transition: background 0.15s;
}

.lep-city-suggestion-item:last-child {
  border-bottom: none;
}

.lep-city-suggestion-item:hover {
  background: #f8f2f6;
}

.lep-city-icon {
  color: #6a1f4c;
  font-size: 18px;
  flex-shrink: 0;
}

.lep-address-locked-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fff8eb;
  border-left: 4px solid #f59e0b;
  padding: 12px 14px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.lep-lock-icon {
  color: #d97706;
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.lep-address-locked-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.lep-address-locked-title {
  font-weight: 700;
  font-size: 13px;
  color: #92400e;
}

.lep-address-locked-text {
  font-size: 12px;
  line-height: 1.4;
  color: #b45309;
  margin: 0;
}

.lep-input--disabled {
  background-color: #f5f2f7 !important;
  color: #8c7b99 !important;
  cursor: not-allowed !important;
  border-color: #e2d5ef !important;
}

.lep-city-text {
  display: flex;
  flex-direction: column;
}

.lep-city-name {
  font-weight: 600;
  font-size: 13px;
  color: #2e1937;
}

.lep-city-subtext {
  font-size: 11px;
  color: #8c7b99;
}

.lep-label {
  font-size: 13px;
  font-weight: 600;
  color: #6a1f4c;
}

.lep-sab-notice {
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: #faf5fd;
  border-left: 3px solid #ab6e93;
  color: #6a1f4c;
  padding: 12px 14px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 8px;
}

.lep-sab-notice i {
  font-size: 18px;
  color: #ab6e93;
  display: inline-flex;
}

.lep-input {
  border: 1px solid #e2d5ef;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 13px;
  color: #3d2a4a;
  background: #fff;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
}

.lep-input:focus { border-color: #6a1f4c; }

.lep-input--error {
  border-color: #de3545 !important;
  box-shadow: 0 0 0 2px rgba(222, 53, 69, 0.15) !important;
}

.lep-facebook-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f8f2f6;
  border: 1px solid #e8dbe8;
  color: #6a1f4c;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-top: 10px;
  line-height: 1.2;

  i {
    font-size: 18px;
    color: #B64985;
  }
}

.lep-textarea-wrapper { position: relative; }

.lep-textarea {
  border: 1px solid #e2d5ef;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 13px;
  color: #3d2a4a;
  background: #fff;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
}

.lep-textarea:focus { border-color: #6a1f4c; }

.lep-char-count {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 11px;
  color: #c0afd0;
  pointer-events: none;
}

/* ─── Score panel ─── */
.lep-score-panel {
  background: #fff;
  border: 1.5px solid #e8daf0;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  box-sizing: border-box;
}

.lep-score-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 700;
  color: #6a1f4c;
}

.lep-score-donut-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.lep-score-percent {
  font-size: 14px;
  font-weight: 600;
  color: #6a1f4c;
  line-height: 1.1;
}

.lep-score-percent small {
  font-size: 11px;
  font-weight: 600;
  color: #6a1f4c;
}

.lep-score-items { display: flex; flex-direction: column; gap: 8px; }

.lep-score-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #3d2a4a;
}

.lep-score-item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
}

.lep-score-item-icon i { font-size: 12px; }

.lep-score-item-icon--ok { background: #e8f5e9; color: #388e3c; }
.lep-score-item-icon--warn { background: #fff8e1; color: #f57f17; }
.lep-score-item-icon--error { background: #fce4ec; color: #c62828; }

.lep-score-item-label { flex: 1; }

.lep-score-item-badge {
  background: #f2e2ee;
  color: #6a1f4c;
  border-radius: 10px;
  padding: 2px 7px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.lep-next-actions {
  border-top: 1px solid #f0e8f6;
  padding-top: 14px;
}

.lep-next-actions-title {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 700;
  color: #6a1f4c;
}

.lep-next-actions-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.lep-next-action-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #3d2a4a;
  /* cursor: pointer; */
  /* transition: color 0.15s; */
}

.lep-next-action-item:hover { 
  /* color: #6a1f4c; */ 
}

.lep-next-action-text { flex: 1; }

.lep-next-action-weight {
  font-size: 10px;
  font-weight: 600;
  color: #6a1f4c;
  background: #f2e2ee;
  padding: 2px 6px;
  border-radius: 4px;
}

.lep-arrow-icon { font-size: 14px; color: #6a1f4c; flex-shrink: 0; }

/* ─── Responsive ─── */
/* Medium: hide preview mockup, keep score panel in 2nd column */
@media (max-width: 1399px) {
  .lep-columns { grid-template-columns: minmax(0, 1fr) 240px; }
  .lep-preview-col { display: none; }
}

/* Small: single column, score panel stacks below form */
@media (max-width: 899px) {
  .lep-columns { grid-template-columns: 1fr; }
  .lep-preview-col { display: none; }
  .lep-score-col { width: 100%; }
}
</style>
